// eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-imports-ts
import { Component, Host, Element, Prop, State, Watch, h, Env } from '@stencil/core';

import { DeviceType } from '../../../../types/base/affiliate/characteristics';
import { AffiliateControllerResponse } from '../../../../types/controllers/affiliate';
import { FormattedPromotion } from '../../types';

import { formatPromotions } from '../../utils';
import { getPlacement } from '../../utils/api';

import { filterPromotions } from '../../utils/filter';
import state, { stencilStore } from '../../utils/store';
import NoFollowLink from '../functional/NoFollowLink';

@Component({
    tag: 'bam-field',
    styleUrl: 'bam-field.css',
    shadow: true,
})
export class BamField {
    @Element() el: HTMLElement;

    // props
    @Prop() placementId: number;
    @Prop() propertyId: number;
    @Prop({ mutable: true }) location: string;
    @Prop() context: string;
    @Prop() field:
        | 'cta'
        | 'title'
        | 'affiliate_name'
        | 'affiliate-name'
        | 'term'
        | 'terms'
        | 'affiliate_terms'
        | 'affiliate-terms'
        | 'bonus';

    // optional characteristics props
    @Prop() pageSlug: string;
    @Prop() subscriptionTier: 0 | 1 | 2 | 3;
    @Prop() wasPro: boolean;
    @Prop() subdivision: number;
    @Prop() league: string;
    @Prop() gameId: number;
    @Prop() ctaType: string;
    @Prop() isMobile: boolean;
    @Prop() deviceType: DeviceType;
    @Prop() isBetsyncUser: boolean;
    @Prop() link = false;

    // optional props for functionality
    @Prop() moduleTitle: string;
    @Prop() defer: boolean;
    @Prop() devMode = false;
    @Prop() initialValue = '';
    @Prop() underline = false;
    @Prop() affiliate: string;
    @Prop() affiliateType: string;
    @Prop() internalId: string;

    // state
    @State() promotions: Array<FormattedPromotion> = [];
    @State() toggle = false;
    @State() bamFields = [];
    @State() loading = true;
    @State() locationToUse: string | null = null;

    @Watch('locationToUse')
    onLocationChange() {
        getPlacement(
            {
                placement_id: this.placementId,
                property_id: this.propertyId,
                location: this.locationToUse,
                page_slug: this.pageSlug,
                subscription_tier: this.subscriptionTier,
                was_pro: this.wasPro,
                subdivision_id: this.subdivision,
                league: this.league,
                game_id: this.gameId,
                cta_type: this.ctaType,
                is_mobile: this.isMobile,
                device_type: this.deviceType,
                isBetsyncUser: this.isBetsyncUser,
                context: this.context,
            },
            this.formatAndSetPromotionsToState.bind(this),
            this.devMode,
            'Field'
        );
    }

    async componentWillLoad() {
        this.locationToUse = this.location || state.location;
        this.bamFields = [...document.querySelectorAll('bam-field')];

        if (Env.BAM_HYDRATE === 'true') {
            await getPlacement(
                {
                    placement_id: this.placementId,
                    property_id: this.propertyId,
                    location: this.locationToUse,
                    page_slug: this.pageSlug,
                    subscription_tier: this.subscriptionTier,
                    was_pro: this.wasPro,
                    subdivision_id: this.subdivision,
                    league: this.league,
                    game_id: this.gameId,
                    cta_type: this.ctaType,
                    is_mobile: this.isMobile,
                    device_type: this.deviceType,
                    isBetsyncUser: this.isBetsyncUser,
                    context: this.context,
                },
                this.formatAndSetPromotionsToState.bind(this),
                this.devMode,
                'Field',
                true
            );
        }
        if (!this.defer && Env.BAM_HYDRATE !== 'true') {
            getPlacement(
                {
                    placement_id: this.placementId,
                    property_id: this.propertyId,
                    location: this.locationToUse,
                    page_slug: this.pageSlug,
                    subscription_tier: this.subscriptionTier,
                    was_pro: this.wasPro,
                    subdivision_id: this.subdivision,
                    league: this.league,
                    game_id: this.gameId,
                    cta_type: this.ctaType,
                    is_mobile: this.isMobile,
                    device_type: this.deviceType,
                    isBetsyncUser: this.isBetsyncUser,
                    context: this.context,
                },
                this.formatAndSetPromotionsToState.bind(this),
                this.devMode,
                'Field'
            );
        }

        // if location is not provided, subscribe to location in store
        stencilStore.onChange('location', newLocation => {
            this.locationToUse = newLocation;
        });
    }

    formatAndSetPromotionsToState(placement: AffiliateControllerResponse, location: string) {
        const promotions = formatPromotions(placement.promotions, {
            allowAltLink: placement.allow_alternate_link,
            promotionListId: placement.id,
            location: location,
            context: this.context,
            propertyId: this.propertyId,
        });

        const filteredPromotions = filterPromotions(
            {
                internal_identifier: this.internalId,
                affiliate_name: this.affiliate,
                affiliate_type: this.affiliateType,
            },
            promotions
        );

        if (!filteredPromotions.length) {
            // eslint-disable-next-line no-console
            console.warn('BAM Promotion List: No active or valid promotions returned');
            this.promotions = filteredPromotions;
            return;
        }

        this.promotions = filteredPromotions.slice(0, 1);
        this.locationToUse = !this.location ? location : this.locationToUse;
        this.loading = false;
    }

    render() {
        if (this.promotions.length === 0) {
            return <Host>{decodeURI(this.initialValue)}</Host>; // The initialValue is set on load in the global.ts file
        }
        let displayValue = '';
        switch (this.field) {
            case 'title':
            case 'cta':
                displayValue = this.promotions[0].cta;
                break;
            case 'affiliate_name':
            case 'affiliate-name':
                displayValue = this.promotions[0].bookName;
                break;
            case 'bonus':
                displayValue = this.promotions[0].bonusCode || 'No Code Needed';
                break;
            case 'term':
            case 'terms':
                displayValue = this.promotions[0].termsText;
                break;
            case 'affiliate-terms':
            case 'affiliate_terms':
                displayValue = this.promotions[0].affiliateTerms;
                break;
        }
        return (
            <Host>
                {this.link ? (
                    <NoFollowLink
                        href={this.promotions[0]?.link}
                        class={`bam-field-link ${this.underline ? 'bam-field-underline' : ''}`}
                        part="link"
                    >
                        {displayValue}
                    </NoFollowLink>
                ) : (
                    displayValue
                )}
            </Host>
        );
    }
}
